import type { RaidGroup, RaidPlayerId } from './raid.ts'

/** Stable identifiers for the six forest objectives on the GvG map. */
export const ForestLocationKey = {
  NorthGrove: 'north_grove',
  EastGrove: 'east_grove',
  SoutheastGrove: 'southeast_grove',
  SouthGrove: 'south_grove',
  SouthwestGrove: 'southwest_grove',
  WestGrove: 'west_grove',
  CentralGrove: 'central_grove',
  NorthwestGrove: 'northwest_grove',
} as const

export type ForestLocationKey =
  (typeof ForestLocationKey)[keyof typeof ForestLocationKey]

/** Persisted position of one raid-snapshot group on the tactical map. */
export interface RaidMapGroupPosition {
  readonly raidGroupId: RaidGroup['id']
  readonly xBasisPoints: number
  readonly yBasisPoints: number
}

/** Persisted position of the shared Tree objective on one raid map. */
export interface RaidMapTreePosition {
  readonly xBasisPoints: number
  readonly yBasisPoints: number
}

/** Legacy per-spawn assignment retained for existing server payloads. */
export interface RaidForestAssignment {
  readonly id: string
  readonly raidPlayerId: RaidPlayerId
  readonly locationKey: ForestLocationKey
  readonly spawnOffsetMinutes: number
}

/** Complete per-raid tactical-map payload returned by `raidMapPlan`. */
export interface RaidMapPlan {
  readonly groupPositions: ReadonlyArray<RaidMapGroupPosition>
  readonly treePosition: RaidMapTreePosition | null
  readonly forestAssignments: ReadonlyArray<RaidForestAssignment>
}

/** A static objective anchor in the shared tactical-map coordinate space. */
export interface MapPointOfInterest {
  readonly label: string
  readonly xBasisPoints: number
  readonly yBasisPoints: number
}

/** A map objective whose assigned players are projected from a stable seeded tag. */
export interface MapTagObjective extends MapPointOfInterest {
  readonly key: string
  readonly tagKey: string
}

/** Static presentation metadata for a forest objective. */
export interface ForestLocation extends MapTagObjective {
  readonly key: ForestLocationKey
}

/** Legacy five-minute spawn metadata retained for server compatibility. */
export interface ForestSpawnSlot {
  readonly offsetMinutes: number
  readonly scheduledAt: string | null
  readonly label: string
}
